import { BookingPeriod } from '@spiaggeit/spit-datepicker'
import { DateTime } from 'luxon'

import { Period , HalfDay } from '@/models/insertPeriod'
import {
  getBookingPeriodFromHalfDay,
  getHalfDayValueFromBookingPeriod,
} from '@/utils/halfDay'

const PENDING_CART_TOKEN_STORAGE_PREFIX = 'pendingCartToken:'
const PENDING_CART_PERIOD_STORAGE_PREFIX = 'pendingCartPeriod:'

export interface PendingCartPeriod {
  end: string
  halfDay: HalfDay
  seasonal: boolean
  start: string
}

export interface PendingCartContext {
  period: PendingCartPeriod
  token: string
}

function getTokenStorageKey(licenseCode: string) {
  return `${PENDING_CART_TOKEN_STORAGE_PREFIX}${licenseCode}`
}

function getPeriodStorageKey(licenseCode: string) {
  return `${PENDING_CART_PERIOD_STORAGE_PREFIX}${licenseCode}`
}

export function setPendingCartContext(
  licenseCode: string,
  context: PendingCartContext
) {
  localStorage.setItem(getTokenStorageKey(licenseCode), context.token)
  localStorage.setItem(
    getPeriodStorageKey(licenseCode),
    JSON.stringify(context.period)
  )
}

export function setPendingCartToken(licenseCode: string, token: string) {
  localStorage.setItem(getTokenStorageKey(licenseCode), token)
}

export function getPendingCartToken(licenseCode: string): string | null {
  return localStorage.getItem(getTokenStorageKey(licenseCode))
}

export function getPendingCartPeriod(
  licenseCode: string
): PendingCartPeriod | null {
  const raw = localStorage.getItem(getPeriodStorageKey(licenseCode))
  if (!raw) {
    return null
  }

  try {
    return JSON.parse(raw) as PendingCartPeriod
  } catch {
    return null
  }
}

export function clearPendingCartContext(licenseCode: string) {
  localStorage.removeItem(getTokenStorageKey(licenseCode))
  localStorage.removeItem(getPeriodStorageKey(licenseCode))
}

export function clearPendingCartToken(licenseCode: string) {
  clearPendingCartContext(licenseCode)
}

export function isPendingCartPeriodMatch(
  period: Period,
  bookingPeriod: BookingPeriod | null,
  pendingPeriod: PendingCartPeriod
): boolean {
  if (
    period.start !== pendingPeriod.start ||
    period.end !== pendingPeriod.end
  ) {
    return false
  }

  if (pendingPeriod.seasonal) {
    return bookingPeriod === BookingPeriod.ALL_SEASON
  }

  return (
    getHalfDayValueFromBookingPeriod(bookingPeriod) === pendingPeriod.halfDay
  )
}

export function pendingCartPeriodFromUnixTimestamps(
  startDate: number,
  endDate: number,
  halfDay: number,
  seasonal: boolean
): PendingCartPeriod | null {
  const start = DateTime.fromSeconds(startDate, { zone: 'utc' }).toISODate()
  const end = DateTime.fromSeconds(endDate, { zone: 'utc' }).toISODate()

  if (!start || !end) {
    return null
  }

  return {
    end,
    halfDay: halfDay as HalfDay,
    seasonal,
    start,
  }
}

export function bookingPeriodFromPendingCartPeriod(
  pendingPeriod: PendingCartPeriod
): BookingPeriod {
  if (pendingPeriod.seasonal) {
    return BookingPeriod.ALL_SEASON
  }

  return getBookingPeriodFromHalfDay(pendingPeriod.halfDay)
}
